import { Reservation } from 'models/reservations';
import { Property } from 'models/properties';

// ── Types ────────────────────────────────────────────────────────────

export interface GuestReservation {
  id: string;
  property: string;
  propertyId: string;
  propertyThumbnail: string;
  code: string;
  channel: string;
  status: string;
  nights: number;
  checkin: Date;
  checkout: Date;
  totalPrice: number;
  currency: string;
  bookedAt: Date | null;
}

export interface MergedGuest {
  id: string;
  name: string;
  email: string;
  phone: string;
  photo?: string;
  country: string;
  totalReservations: number;
  latestReservation: Date;
  firstBooking: Date;
  preferredChannel: string;
  totalSpent: number;
  /** Per-currency spending breakdown, e.g. { AMD: 2070168, USD: 350 } */
  spentByCurrency: Record<string, number>;
  avgNights: number;
  reservations: GuestReservation[];
}

// ── Country / location normalization ─────────────────────────────────
//
// The channel-manager API returns guest.location in various messy formats.
// This normalizer extracts COUNTRY ONLY (no cities) so widgets can
// aggregate cleanly. It's designed to be EXTENSIBLE — just add entries
// to the lookup tables below when new variants appear in the data.
//
// Known patterns (add new ones as they arise):
//   "., AM"               → "Armenia"          (dot-junk + ISO code)
//   "., PT"               → "Portugal"
//   "RU"                  → "Russia"            (bare ISO code)
//   "US"                  → "United States"
//   "Boston, MA"          → "United States"     (city + US state → country)
//   "Yerevan, Armenia"    → "Armenia"           (city + country → country)
//   "Dubai, United Arab Emirates" → "United Arab Emirates"
//   ""                    → ""                   (empty → unknown)

/** ISO 3166-1 alpha-2 → full country name. Extend as needed. */
const COUNTRY_CODE_MAP: Record<string, string> = {
  AD: 'Andorra', AE: 'United Arab Emirates', AF: 'Afghanistan', AL: 'Albania',
  AM: 'Armenia', AO: 'Angola', AR: 'Argentina', AT: 'Austria', AU: 'Australia',
  AZ: 'Azerbaijan', BA: 'Bosnia and Herzegovina', BB: 'Barbados', BD: 'Bangladesh',
  BE: 'Belgium', BG: 'Bulgaria', BH: 'Bahrain', BO: 'Bolivia', BR: 'Brazil',
  BY: 'Belarus', BZ: 'Belize',
  CA: 'Canada', CH: 'Switzerland', CL: 'Chile', CN: 'China', CO: 'Colombia',
  CR: 'Costa Rica', CU: 'Cuba', CY: 'Cyprus', CZ: 'Czech Republic',
  DE: 'Germany', DK: 'Denmark', DO: 'Dominican Republic', DZ: 'Algeria',
  EC: 'Ecuador', EE: 'Estonia', EG: 'Egypt', ES: 'Spain', ET: 'Ethiopia',
  FI: 'Finland', FK: 'Falkland Islands', FR: 'France',
  GB: 'United Kingdom', GE: 'Georgia', GH: 'Ghana', GR: 'Greece',
  GT: 'Guatemala', GY: 'Guyana',
  HK: 'Hong Kong', HN: 'Honduras', HR: 'Croatia', HT: 'Haiti', HU: 'Hungary',
  ID: 'Indonesia', IE: 'Ireland', IL: 'Israel', IN: 'India',
  IQ: 'Iraq', IR: 'Iran', IS: 'Iceland', IT: 'Italy', JM: 'Jamaica',
  JO: 'Jordan', JP: 'Japan', KE: 'Kenya', KG: 'Kyrgyzstan', KH: 'Cambodia',
  KR: 'South Korea', KW: 'Kuwait', KZ: 'Kazakhstan',
  LA: 'Laos', LB: 'Lebanon', LK: 'Sri Lanka', LT: 'Lithuania',
  LU: 'Luxembourg', LV: 'Latvia', LY: 'Libya',
  MA: 'Morocco', MD: 'Moldova', ME: 'Montenegro', MK: 'North Macedonia',
  MM: 'Myanmar', MN: 'Mongolia', MO: 'Macau', MT: 'Malta', MU: 'Mauritius',
  MV: 'Maldives', MW: 'Malawi', MX: 'Mexico', MY: 'Malaysia',
  NG: 'Nigeria', NI: 'Nicaragua', NL: 'Netherlands', NO: 'Norway', NP: 'Nepal',
  NZ: 'New Zealand',
  OM: 'Oman', PA: 'Panama', PE: 'Peru', PH: 'Philippines', PK: 'Pakistan',
  PL: 'Poland', PR: 'Puerto Rico', PT: 'Portugal', PY: 'Paraguay',
  QA: 'Qatar', RO: 'Romania', RS: 'Serbia', RU: 'Russia', RW: 'Rwanda',
  SA: 'Saudi Arabia', SD: 'Sudan', SE: 'Sweden', SG: 'Singapore', SI: 'Slovenia',
  SK: 'Slovakia', SV: 'El Salvador', SY: 'Syria',
  TH: 'Thailand', TJ: 'Tajikistan', TM: 'Turkmenistan', TN: 'Tunisia',
  TR: 'Turkey', TW: 'Taiwan', TZ: 'Tanzania',
  UA: 'Ukraine', UG: 'Uganda', US: 'United States', UY: 'Uruguay', UZ: 'Uzbekistan',
  VE: 'Venezuela', VN: 'Vietnam', XK: 'Kosovo', ZA: 'South Africa', ZW: 'Zimbabwe',
};

/** Reverse lookup: full country name → ISO code */
const COUNTRY_NAME_TO_CODE: Record<string, string> = {};
Object.entries(COUNTRY_CODE_MAP).forEach(([code, name]) => {
  COUNTRY_NAME_TO_CODE[name.toLowerCase()] = code;
});

/** US state / territory codes — prevents "Boston, MA" → Morocco */
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);

/**
 * Convert an ISO 3166-1 alpha-2 code to a flag emoji.
 * Works by mapping each letter to its Regional Indicator Symbol.
 * Returns 🌍 for unknown/empty.
 */
export function countryFlag(isoCode: string): string {
  if (!isoCode || isoCode.length !== 2) return '\u{1F30D}'; // 🌍
  const [a, b] = isoCode.toUpperCase().split('');
  return String.fromCodePoint(
    a.charCodeAt(0) - 65 + 0x1F1E6,
    b.charCodeAt(0) - 65 + 0x1F1E6,
  );
}

/**
 * Get flag emoji for a country name. Returns 🌍 for unknown.
 */
export function flagForCountry(country: string): string {
  if (!country) return '\u{1F30D}';
  const code = COUNTRY_NAME_TO_CODE[country.toLowerCase()];
  return code ? countryFlag(code) : '\u{1F30D}';
}

/**
 * Normalize messy location strings to COUNTRY ONLY.
 *
 * Strips cities, resolves ISO codes and US states, returns just the
 * country name for clean aggregation in widgets and charts.
 *
 * To handle a new pattern, add it as a step below and document the variant
 * in the comment block at the top of this section.
 */
function normalizeLocation(raw: string | null | undefined): string {
  if (!raw) return '';
  const loc = raw.trim();
  if (!loc) return '';

  // Step 1: "., XX" or ". , XX" — junk prefix + 2-letter code
  const dotCodeMatch = loc.match(/^[.,\s]+([A-Z]{2})$/);
  if (dotCodeMatch) {
    return COUNTRY_CODE_MAP[dotCodeMatch[1]] || dotCodeMatch[1];
  }

  // Step 2: bare 2-letter code like "RU", "US"
  if (/^[A-Z]{2}$/.test(loc)) {
    return COUNTRY_CODE_MAP[loc] || loc;
  }

  // Step 3: "City, XX" — could be US state or country code
  const cityCodeMatch = loc.match(/^(.+),\s*([A-Z]{2})$/);
  if (cityCodeMatch) {
    const code = cityCodeMatch[2];

    // US state → "United States"
    if (US_STATE_CODES.has(code)) {
      return 'United States';
    }

    // Country code → country name
    const fullCountry = COUNTRY_CODE_MAP[code];
    if (fullCountry) {
      return fullCountry;
    }
  }

  // Step 4: "City, Country Name" — extract country part
  const cityCountryMatch = loc.match(/^.+,\s*(.+)$/);
  if (cityCountryMatch) {
    const possibleCountry = cityCountryMatch[1].trim();
    // Verify it's a known country name
    if (COUNTRY_NAME_TO_CODE[possibleCountry.toLowerCase()]) {
      return possibleCountry;
    }
  }

  // Step 5: Check if the entire string is a known country name
  if (COUNTRY_NAME_TO_CODE[loc.toLowerCase()]) {
    return loc;
  }

  return loc;
}

// ── Phone calling code → country ─────────────────────────────────────
//
// When a guest has no location data but has a phone number with an
// international prefix, we can infer their country from the calling code.
// Codes are matched longest-first so e.g. +374 (Armenia) beats +37 (nothing).
// All countries here also appear in COUNTRY_CODE_MAP for flag support.

const CALLING_CODE_TO_COUNTRY: [string, string][] = [
  // 4-digit codes first (NANPA +1xxx overlays)
  ['1246', 'Barbados'],
  ['1809', 'Dominican Republic'], ['1829', 'Dominican Republic'], ['1849', 'Dominican Republic'],
  ['1876', 'Jamaica'],
  ['1787', 'Puerto Rico'], ['1939', 'Puerto Rico'],
  // 3-digit codes — sorted by prefix to avoid shadowing
  ['212', 'Morocco'], ['213', 'Algeria'], ['216', 'Tunisia'], ['218', 'Libya'],
  ['230', 'Mauritius'], ['233', 'Ghana'], ['234', 'Nigeria'],
  ['244', 'Angola'], ['249', 'Sudan'],
  ['250', 'Rwanda'], ['251', 'Ethiopia'], ['254', 'Kenya'],
  ['255', 'Tanzania'], ['256', 'Uganda'], ['260', 'Zambia'],
  ['263', 'Zimbabwe'], ['265', 'Malawi'],
  ['351', 'Portugal'], ['352', 'Luxembourg'], ['353', 'Ireland'],
  ['354', 'Iceland'], ['355', 'Albania'], ['356', 'Malta'],
  ['357', 'Cyprus'], ['358', 'Finland'], ['359', 'Bulgaria'],
  ['370', 'Lithuania'], ['371', 'Latvia'], ['372', 'Estonia'],
  ['373', 'Moldova'], ['374', 'Armenia'], ['375', 'Belarus'],
  ['376', 'Andorra'],
  ['380', 'Ukraine'], ['381', 'Serbia'], ['382', 'Montenegro'],
  ['385', 'Croatia'], ['386', 'Slovenia'],
  ['387', 'Bosnia and Herzegovina'], ['389', 'North Macedonia'],
  ['420', 'Czech Republic'], ['421', 'Slovakia'],
  ['500', 'Falkland Islands'],
  ['501', 'Belize'], ['502', 'Guatemala'], ['503', 'El Salvador'],
  ['504', 'Honduras'], ['505', 'Nicaragua'], ['506', 'Costa Rica'],
  ['507', 'Panama'],
  ['509', 'Haiti'],
  ['591', 'Bolivia'], ['592', 'Guyana'], ['593', 'Ecuador'],
  ['595', 'Paraguay'], ['598', 'Uruguay'],
  ['852', 'Hong Kong'], ['853', 'Macau'],
  ['855', 'Cambodia'], ['856', 'Laos'],
  ['880', 'Bangladesh'], ['886', 'Taiwan'],
  ['960', 'Maldives'], ['961', 'Lebanon'], ['962', 'Jordan'],
  ['963', 'Syria'], ['964', 'Iraq'], ['965', 'Kuwait'],
  ['966', 'Saudi Arabia'], ['968', 'Oman'],
  ['971', 'United Arab Emirates'], ['972', 'Israel'],
  ['973', 'Bahrain'], ['974', 'Qatar'], ['976', 'Mongolia'],
  ['977', 'Nepal'],
  ['992', 'Tajikistan'], ['993', 'Turkmenistan'],
  ['994', 'Azerbaijan'], ['995', 'Georgia'],
  ['996', 'Kyrgyzstan'], ['998', 'Uzbekistan'],
  // 2-digit codes
  ['20', 'Egypt'], ['27', 'South Africa'],
  ['30', 'Greece'], ['31', 'Netherlands'], ['32', 'Belgium'],
  ['33', 'France'], ['34', 'Spain'], ['36', 'Hungary'],
  ['39', 'Italy'], ['40', 'Romania'], ['41', 'Switzerland'],
  ['43', 'Austria'], ['44', 'United Kingdom'], ['45', 'Denmark'],
  ['46', 'Sweden'], ['47', 'Norway'], ['48', 'Poland'], ['49', 'Germany'],
  ['51', 'Peru'], ['52', 'Mexico'], ['53', 'Cuba'],
  ['54', 'Argentina'], ['55', 'Brazil'], ['56', 'Chile'],
  ['57', 'Colombia'], ['58', 'Venezuela'],
  ['60', 'Malaysia'], ['61', 'Australia'], ['62', 'Indonesia'],
  ['63', 'Philippines'], ['64', 'New Zealand'], ['65', 'Singapore'],
  ['66', 'Thailand'],
  ['81', 'Japan'], ['82', 'South Korea'], ['84', 'Vietnam'],
  ['86', 'China'], ['90', 'Turkey'], ['91', 'India'],
  ['92', 'Pakistan'], ['93', 'Afghanistan'], ['94', 'Sri Lanka'],
  ['95', 'Myanmar'], ['98', 'Iran'],
  // 1-digit codes (must be last — catch-all for NANPA and CIS)
  // Note: +7 is shared by Russia & Kazakhstan — we default to Russia
  // as it covers the majority. +77xx is technically KZ but ambiguous.
  // Note: +1 is shared by US, Canada, Caribbean — we default to US.
  ['7', 'Russia'],
  ['1', 'United States'],
];

/**
 * Infer country from a phone number's international calling code.
 * Strips non-digits, then matches longest prefix first.
 * Returns country name or '' if no match.
 */
export function countryFromPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) return '';

  for (const [prefix, country] of CALLING_CODE_TO_COUNTRY) {
    if (digits.startsWith(prefix)) return country;
  }
  return '';
}

// ── Internal email domain blocklist ──────────────────────────────────
//
// Reservations with emails from these domains are COMPLETELY EXCLUDED
// from all analytics (KPIs, charts, guest list). Staff sometimes enter
// their work email when creating owner-placeholder or test reservations.
//
// Extend this set when new internal domains appear.

const INTERNAL_EMAIL_DOMAINS = new Set([
  'hotelise.com',
]);

/**
 * Check if a guest email belongs to an internal/staff domain.
 * Case-insensitive. Returns true for staff emails like hermine@hotelise.com.
 */
function isInternalEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) return false;
  const domain = trimmed.split('@').pop() || '';
  return INTERNAL_EMAIL_DOMAINS.has(domain);
}

/**
 * Check whether an email is valid for guest merge matching.
 * Returns false for: missing, no @, or internal/staff domains.
 */
function isValidMergeEmail(email: string | null | undefined): email is string {
  if (!email) return false;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) return false;
  return !isInternalEmail(trimmed);
}

// ── Phone normalization ──────────────────────────────────────────────

/** Strip all non-digit chars to compare phone numbers regardless of formatting */
function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  // At least 7 digits to be considered a valid phone for matching
  return digits.length >= 7 ? digits : '';
}

// ── Union-Find ───────────────────────────────────────────────────────

class UnionFind {
  private parent: Map<string, string> = new Map();

  find(x: string): string {
    if (!this.parent.has(x)) {
      this.parent.set(x, x);
    }
    let root = x;
    while (this.parent.get(root) !== root) {
      root = this.parent.get(root)!;
    }
    // Path compression
    let current = x;
    while (current !== root) {
      const next = this.parent.get(current)!;
      this.parent.set(current, root);
      current = next;
    }
    return root;
  }

  union(a: string, b: string): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA !== rootB) {
      this.parent.set(rootB, rootA);
    }
  }
}

// ── Guest merging ────────────────────────────────────────────────────

/**
 * Merge reservations into deduplicated guests by:
 * 1. Guest ID (primary key)
 * 2. Email match (case-insensitive)
 * 3. Phone match (digits-only comparison)
 */
export function mergeGuests(
  reservations: Reservation[],
  propertiesMap: Record<string, Property>,
): MergedGuest[] {
  if (reservations.length === 0) return [];

  // Filter out owner placeholders and internal/staff reservations.
  // 1. base_price (accommodation fee) === 0 → owner stay (cleaning fee may
  //    still be charged, so subtotal/total_price can be > 0).
  // 2. Guest email from an internal domain (@hotelise.com) → staff-created
  //    test or placeholder reservation.
  // Both types are COMPLETELY EXCLUDED from all KPIs and charts.
  const realReservations = reservations.filter((r) => {
    const accommodation = r.base_price ?? r.subtotal ?? r.total_price;
    if (accommodation <= 0) return false;
    if (isInternalEmail(r.guest?.email)) return false;
    return true;
  });
  if (realReservations.length === 0) return [];

  const uf = new UnionFind();
  const emailToKey = new Map<string, string>();
  const phoneToKey = new Map<string, string>();
  const reservationsByKey = new Map<string, Reservation[]>();

  // Phase 1: Assign each reservation a guest key and merge via email/phone
  realReservations.forEach((r) => {
    const { guest } = r;
    if (!guest) return;

    const guestKey = `guest:${guest.id}`;
    uf.find(guestKey); // ensure key exists

    // Merge by email — skip placeholder / invalid / staff emails
    const email = guest.email?.trim().toLowerCase();
    if (isValidMergeEmail(email)) {
      const existing = emailToKey.get(email);
      if (existing) {
        uf.union(existing, guestKey);
      } else {
        emailToKey.set(email, guestKey);
      }
    }

    // Merge by phone
    const phone = normalizePhone(guest.phone || '');
    if (phone) {
      const existing = phoneToKey.get(phone);
      if (existing) {
        uf.union(existing, guestKey);
      } else {
        phoneToKey.set(phone, guestKey);
      }
    }

    // Collect reservations per key (will group by root later)
    if (!reservationsByKey.has(guestKey)) {
      reservationsByKey.set(guestKey, []);
    }
    reservationsByKey.get(guestKey)!.push(r);
  });

  // Phase 2: Group reservations by root key
  const groupedReservations = new Map<string, Reservation[]>();
  reservationsByKey.forEach((resArr, key) => {
    const root = uf.find(key);
    if (!groupedReservations.has(root)) {
      groupedReservations.set(root, []);
    }
    groupedReservations.get(root)!.push(...resArr);
  });

  // Phase 3: Build MergedGuest objects
  const guests: MergedGuest[] = [];
  groupedReservations.forEach((resArr, rootKey) => {
    // Use the most recent reservation's guest data as primary
    const sorted = [...resArr].sort(
      (a, b) => new Date(b.checkin_time || b.start_date).getTime() -
        new Date(a.checkin_time || a.start_date).getTime(),
    );
    const primary = sorted[0].guest;

    // Deduplicate reservations by code (same reservation can appear in multiple pages)
    const seenCodes = new Set<string>();
    const uniqueRes: GuestReservation[] = [];
    sorted.forEach((r) => {
      if (seenCodes.has(r.code)) return;
      seenCodes.add(r.code);

      const prop = propertiesMap[r.listing?.property_id];
      uniqueRes.push({
        id: r.id,
        property: prop?.name || r.listing?.nickname || r.listing?.name || 'Unknown',
        propertyId: String(r.listing?.property_id || ''),
        propertyThumbnail: prop?.picture || r.listing?.picture_url || '',
        code: r.code,
        channel: r.channel || 'manual',
        status: r.status || 'accepted',
        nights: r.nights || 0,
        checkin: new Date(r.checkin_time || r.start_date),
        checkout: new Date(r.checkout_time || r.end_date),
        totalPrice: r.total_price || 0,
        currency: (r.currency || '').trim() || 'UNKNOWN',
        bookedAt: r.createdAt ? new Date(r.createdAt) : null,
      });
    });

    const totalNights = uniqueRes.reduce((s, r) => s + r.nights, 0);

    // Find preferred channel (most common) and per-currency spending
    const channelCounts: Record<string, number> = {};
    const spentByCurrency: Record<string, number> = {};
    uniqueRes.forEach((r) => {
      channelCounts[r.channel] = (channelCounts[r.channel] || 0) + 1;
      spentByCurrency[r.currency] = (spentByCurrency[r.currency] || 0) + r.totalPrice;
    });
    const preferredChannel = Object.entries(channelCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || 'manual';

    guests.push({
      id: rootKey,
      name: `${primary.first_name || ''} ${primary.last_name || ''}`.trim() || 'Unknown Guest',
      email: primary.email || '',
      phone: primary.phone || '',
      photo: primary.picture_url || undefined,
      country: normalizeLocation(primary.location) || countryFromPhone(primary.phone),
      totalReservations: uniqueRes.length,
      latestReservation: uniqueRes[0]?.checkin || new Date(),
      firstBooking: uniqueRes[uniqueRes.length - 1]?.checkin || new Date(),
      preferredChannel,
      totalSpent: uniqueRes.reduce((s, r) => s + r.totalPrice, 0),
      spentByCurrency,
      avgNights: uniqueRes.length > 0 ? Math.round(totalNights / uniqueRes.length) : 0,
      reservations: uniqueRes,
    });
  });

  // Sort by total bookings descending (most loyal guests first)
  guests.sort((a, b) => b.totalReservations - a.totalReservations);

  return guests;
}

// ── Country distribution with top N + Other ──────────────────────────

export interface CountryDistItem {
  name: string;
  value: number;
  color: string;
}

const DISTRIBUTION_COLORS = [
  'bg-[hsl(210,80%,55%)]', 'bg-[hsl(350,80%,60%)]', 'bg-[hsl(45,90%,55%)]',
  'bg-[hsl(160,50%,50%)]', 'bg-[hsl(280,60%,60%)]', 'bg-[hsl(30,80%,55%)]',
  'bg-[hsl(190,70%,50%)]', 'bg-[hsl(0,60%,50%)]',
];

/** Prepend flag emoji to a country name for display */
function withFlag(country: string): string {
  if (!country || country === 'Unknown') return `\u{1F30D} Unknown`;
  if (country === 'Other') return `\u{1F30D} Other`;
  return `${flagForCountry(country)} ${country}`;
}

export function buildCountryDistribution(
  guests: MergedGuest[],
  maxItems = 7,
): CountryDistItem[] {
  const map: Record<string, number> = {};
  guests.forEach((g) => {
    const country = g.country || 'Unknown';
    map[country] = (map[country] || 0) + 1;
  });

  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);

  if (sorted.length <= maxItems) {
    return sorted.map(([name, count], i) => ({
      name: withFlag(name),
      value: count,
      color: DISTRIBUTION_COLORS[i % DISTRIBUTION_COLORS.length],
    }));
  }

  // Top N + Other
  const top = sorted.slice(0, maxItems);
  const otherCount = sorted.slice(maxItems).reduce((s, [, c]) => s + c, 0);

  const items = top.map(([name, count], i) => ({
    name: withFlag(name),
    value: count,
    color: DISTRIBUTION_COLORS[i % DISTRIBUTION_COLORS.length],
  }));

  if (otherCount > 0) {
    items.push({
      name: `\u{1F30D} Other`,
      value: otherCount,
      color: 'bg-[hsl(0,0%,55%)]',
    });
  }

  return items;
}
